import { useState, useEffect, useCallback, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import api, { fmt, fmtQty, fmtInt, fmtDate } from '../../utils/api';
import { startRegistration } from '@simplewebauthn/browser';
import * as XLSX from 'xlsx';

const MONTHS_HE = ['ינואר','פברואר','מרץ','אפריל','מאי','יוני','יולי','אוגוסט','ספטמבר','אוקטובר','נובמבר','דצמבר'];
const MONTHS_EN = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function monthLabel(m, lang) {
  const idx = parseInt(m.split('-')[1]) - 1;
  return lang === 'he' ? MONTHS_HE[idx] : MONTHS_EN[idx];
}

export default function DashboardPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const he = lang === 'he';
  const currentYear = new Date().getFullYear();
  const currentQ = `Q${Math.ceil((new Date().getMonth() + 1) / 3)}`;

  const [year] = useState(currentYear);
  const [quarter, setQuarter] = useState('YTD');
  const [view, setView] = useState('overview'); // 'overview' | 'mc'
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCard, setExpandedCard] = useState(null);
  const [profitDetail, setProfitDetail] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/dashboard?year=${year}&quarter=${quarter}`);
      setData(res.data);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [year, quarter]);

  const [showSecurity, setShowSecurity] = useState(false);
  const [bioStatus, setBioStatus] = useState(null);

  useEffect(() => { fetchData(); setExpandedCard(null); }, [fetchData]);

  if (loading) return <div className="loading"><div className="spinner" />{t('common.loading')}</div>;
  if (!data) return <div className="empty-state">{t('common.noData')}</div>;

  const { kpis, futureKpis, months, details } = data;
  const f = futureKpis || { units: 0, customerRevenue: 0, invoiceIncome: 0, cogs: 0, profit: 0, margin: 0, expectedCredit45: 0, expectedCredit175: 0, totalExpectedProfit: 0, customerRebate: 0 };
  const toggle = async (key) => {
    if (expandedCard === key) { setExpandedCard(null); return; }
    setExpandedCard(key);
    if ((key === 'mProfit' || key === 'cProfit') && !profitDetail) {
      try {
        const res = await api.get('/profitability/cellcom-orders');
        const orders = Array.isArray(res.data) ? res.data : (res.data.orders || []);
        setProfitDetail(orders.filter(o => o.status !== 'cancelled' && o.status !== 'pending' && o.status !== 'ready'));
      } catch {}
    }
  };

  const periodLabel = quarter === 'YTD' ? (he ? `${year} מתחילת השנה` : `${year} Year To Date`) : `${quarter} ${year}`;

  const kpiCards = [
    {
      key: 'arrived',
      label: he ? 'הגיעו לישראל' : 'Arrived to Israel',
      value: fmtQty(kpis.arrived.units),
      sub: `${he ? "יח'" : 'units'} · ${fmtInt(kpis.arrived.value)}`,
      sub2: kpis.arrived.stOriginalUnits > 0 ? `S&T ${he ? 'פתיחה' : 'opening'}: ${fmtQty(kpis.arrived.stOriginalUnits)} · ${fmtInt(kpis.arrived.stOriginalValue)}` : null,
      color: 'clear-blue',
    },
    {
      key: 'transit',
      label: he ? 'בדרך' : 'In Transit',
      value: fmtQty(kpis.inTransit.units),
      sub: `${he ? "יח'" : 'units'} · ${fmtInt(kpis.inTransit.value)}`,
      color: 'clear-blue',
      badge: kpis.inTransit.units > 0 ? (he ? '🚢 בדרך' : '🚢 On the way') : null,
      badgeColor: '#fff3e0', badgeText: '#e65100',
    },
    {
      key: 'sold',
      label: he ? 'נמכרו (יצאו)' : 'Sold (Shipped Out)',
      value: fmtQty(kpis.sold.units),
      sub: `${he ? "יח'" : 'units'} · ${fmtInt(kpis.sold.value)}`,
      color: 'clear-blue',
    },
    {
      key: 'stock',
      label: he ? 'מלאי נוכחי' : 'Current Stock',
      value: fmtQty(kpis.stock.units),
      sub: `S&T: ${fmtQty(kpis.stock.stUnits)} · Shager: ${fmtQty(kpis.stock.shagerUnits)}`,
      color: 'clear-blue',
    },
    {
      key: 'stockValue',
      label: he ? 'שווי מלאי' : 'Stock Value',
      value: fmtInt(kpis.stock.value),
      sub: he ? 'במחיר רכישה' : 'at purchase price',
      color: 'blue-fill',
    },
    // NOTE: kpiCards is consumed only via .slice(0,5) for the Inventory Flow row above.
    // The former indices 5+ (quarterlyCredits/revenue/credits/dealCreditEarned/profit/
    // totalProfit/chainProfit) were never rendered — the Current/Total sections use
    // their own literal card arrays below. Removed as dead code (re-audit 2026-06-22).
  ];

  const renderExpandedDetail = (key) => {
    if (!details) return null;

    if (key === 'arrived') {
      return (<>
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table>
            <thead><tr>
              <th>{he ? 'הזמנה' : 'Order'}</th>
              <th>{he ? 'תאריך' : 'Date'}</th>
              <th>{he ? 'מקור' : 'Origin'}</th>
              <th className="text-end">{he ? "יח'" : 'Units'}</th>
              <th className="text-end">{he ? 'סכום' : 'Value'}</th>
            </tr></thead>
            <tbody>
              {details.arrived.map((o, i) => (
                <tr key={i}>
                  <td className="fw-bold" style={{ fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{o.orderNo}</td>
                  <td>{fmtDate(o.date)}</td>
                  <td><span className={`badge badge-${(o.origin || '').toLowerCase()}`}>{o.origin}</span></td>
                  <td className="text-end" style={{ fontFamily: "'DM Mono', monospace" }}>{fmtQty(o.units)}</td>
                  <td className="text-end" style={{ fontFamily: "'DM Mono', monospace" }}>{fmtInt(o.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {kpis.arrived.stOriginalUnits > 0 && (
          <div className="formula" style={{ marginTop: 12 }}>
            <div><span className="lbl">{he ? 'הגיעו מהזמנות' : 'From Orders'}</span><span className="val">{fmtQty(kpis.arrived.units)} {he ? "יח'" : 'units'}</span></div>
            <span className="op">+</span>
            <div><span className="lbl">{he ? 'מלאי פתיחה S&T' : 'S&T Opening Stock'}</span><span className="val">{fmtQty(kpis.arrived.stOriginalUnits)} {he ? "יח'" : 'units'} · {fmtInt(kpis.arrived.stOriginalValue)}</span></div>
            <span className="op">=</span>
            <div><span className="lbl">{he ? 'סה"כ זמין' : 'Total Available'}</span><span className="val" style={{ color: 'var(--green)' }}>{fmtQty(kpis.arrived.units + kpis.arrived.stOriginalUnits)} {he ? "יח'" : 'units'}</span></div>
          </div>
        )}
      </>);
    }

    if (key === 'transit') {
      return (
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table>
            <thead><tr>
              <th>{he ? 'הזמנה' : 'Order'}</th>
              <th>{he ? 'תאריך' : 'Date'}</th>
              <th>{he ? 'מקור' : 'Origin'}</th>
              <th className="text-end">{he ? "יח'" : 'Units'}</th>
              <th className="text-end">{he ? 'סכום' : 'Value'}</th>
              <th>{he ? 'הגעה צפויה' : 'ETA'}</th>
            </tr></thead>
            <tbody>
              {(details.transit || []).map((o, i) => (
                <tr key={i}>
                  <td className="fw-bold" style={{ fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{o.orderNo}</td>
                  <td>{fmtDate(o.date)}</td>
                  <td><span className={`badge badge-${(o.origin || '').toLowerCase()}`}>{o.origin}</span></td>
                  <td className="text-end" style={{ fontFamily: "'DM Mono', monospace" }}>{fmtQty(o.units)}</td>
                  <td className="text-end" style={{ fontFamily: "'DM Mono', monospace" }}>{fmtInt(o.value)}</td>
                  <td>{o.arrivalDate ? fmtDate(o.arrivalDate) : '—'} {o.transport === 'AIR' ? '✈' : '🚢'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (key === 'stock' || key === 'stockValue') {
      return (
        <div style={{ marginTop: 12, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <div className="formula">
            <div><span className="lbl">S&T</span><span className="val">{fmtQty(kpis.stock.stUnits)} {he ? "יח'" : 'units'}</span></div>
            <span className="op">+</span>
            <div><span className="lbl">Shager</span><span className="val">{fmtQty(kpis.stock.shagerUnits)} {he ? "יח'" : 'units'}</span></div>
            <span className="op">=</span>
            <div><span className="lbl">{he ? 'סה"כ' : 'Total'}</span><span className="val">{fmtQty(kpis.stock.units)} {he ? "יח'" : 'units'}</span></div>
            <span className="op">·</span>
            <div><span className="lbl">{he ? 'שווי' : 'Value'}</span><span className="val" style={{ color: 'var(--gold)' }}>{fmtInt(kpis.stock.value)}</span></div>
          </div>
        </div>
      );
    }

    if (key === 'revenue') {
      return (
        <div className="formula" style={{ marginTop: 12 }}>
          <div><span className="lbl">{he ? 'מכירות ללקוח' : 'Customer Sales'}</span><span className="val">{fmtInt(kpis.revenue)}</span></div>
          <span className="op">→</span>
          <div><span className="lbl">{he ? 'הכנסות שגר (C price)' : 'Shager Income (C price)'}</span><span className="val" style={{ color: 'var(--green)' }}>{fmtInt(kpis.invoiceIncome)}</span></div>
          <span className="op">·</span>
          <div><span className="lbl">{he ? 'מרווח' : 'Margin'}</span><span className="val">{(kpis.margin || 0).toFixed(1)}%</span></div>
        </div>
      );
    }

    if (key === 'sold') {
      return (
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table>
            <thead><tr>
              <th>{he ? 'הזמנה' : 'Order'}</th>
              <th>{he ? 'תאריך' : 'Date'}</th>
              <th>{he ? 'שינוע' : 'Transport'}</th>
              <th>{he ? 'סטטוס' : 'Status'}</th>
              <th className="text-end">{he ? "יח'" : 'Units'}</th>
              <th className="text-end">{he ? 'הכנסה' : 'Revenue'}</th>
            </tr></thead>
            <tbody>
              {details.sold.map((o, i) => (
                <tr key={i}>
                  <td className="fw-bold" style={{ fontFamily: "'DM Mono', monospace", fontSize: 12 }}>#{o.orderNumber}</td>
                  <td>{fmtDate(o.date)}</td>
                  <td>{o.transport === 'AIR' ? '✈ Air' : '🚢 Ocean'}</td>
                  <td><span className="badge" style={{
                    background: o.status === 'shipped' || o.status === 'complete' ? '#e8f5e9' : '#fff3e0',
                    color: o.status === 'shipped' || o.status === 'complete' ? '#2e7d32' : '#e65100',
                  }}>{o.status}</span></td>
                  <td className="text-end" style={{ fontFamily: "'DM Mono', monospace" }}>{fmtQty(o.units)}</td>
                  <td className="text-end" style={{ fontFamily: "'DM Mono', monospace" }}>{fmtInt(o.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (key === 'credits') {
      return (
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table>
            <thead><tr>
              <th>{he ? 'הזמנה' : 'Order'}</th>
              <th>{he ? 'תיאור' : 'Description'}</th>
              <th>{he ? 'תאריך' : 'Date'}</th>
              <th className="text-end">{he ? 'סכום' : 'Amount'}</th>
            </tr></thead>
            <tbody>
              {details.credits.map((c, i) => (
                <tr key={i}>
                  <td className="fw-bold" style={{ fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{c.orderNo}</td>
                  <td>{c.description}</td>
                  <td>{fmtDate(c.date)}</td>
                  <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", color: 'var(--blue)' }}>{fmtInt(c.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (key === 'dealCreditEarned') {
      const deals = kpis.dealsBreakdown || [];
      if (deals.length === 0) {
        return <div className="empty-state">{he ? 'אין דילים פעילים' : 'No active deals'}</div>;
      }
      const tEarned = deals.reduce((s, d) => s + (d.earned || 0), 0);
      const tReceived = deals.reduce((s, d) => s + (d.received || 0), 0);
      const tOwed = deals.reduce((s, d) => s + (d.owed || 0), 0);
      const tValue = deals.reduce((s, d) => s + (d.dealValue || 0), 0);
      return (<>
        <h4 style={{ marginBottom: 8, color: 'var(--text-muted)', fontSize: 13 }}>
          {he ? 'פירוט פר דיל' : 'Per-Deal Breakdown'}
        </h4>
        <div className="table-wrap">
          <table>
            <thead><tr>
              <th>{he ? 'דיל' : 'Deal'}</th>
              <th>{he ? 'סוג' : 'Type'}</th>
              <th className="text-end">{he ? 'חלק שגר' : 'Share'}</th>
              <th className="text-end">{he ? 'סך הדיל' : 'Total Value'}</th>
              <th className="text-end">{he ? 'הרווחנו' : 'Earned'}</th>
              <th className="text-end">{he ? 'התקבל' : 'Received'}</th>
              <th className="text-end">{he ? 'חייבים לנו' : 'Owed'}</th>
            </tr></thead>
            <tbody>
              {deals.map(d => (
                <tr key={d.id}>
                  <td className="fw-bold">
                    {d.name}
                    {d.targetReached && <span style={{ marginInlineStart: 6, padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: '#dcfce7', color: '#166534' }}>✓ {he ? 'הושלם' : 'reached'}</span>}
                  </td>
                  <td>{d.type === 'fixed' ? (he ? 'קבוע' : 'fixed') : (he ? 'שוטף' : 'ongoing')}</td>
                  <td className="text-end">{d.shareRatio != null ? `${(parseFloat(d.shareRatio) * 100).toFixed(0)}%` : '—'}</td>
                  <td className="text-end">{d.dealValue ? fmtInt(d.dealValue) : '—'}</td>
                  <td className="text-end fw-bold" style={{ color: '#166534' }}>{fmtInt(d.earned)}</td>
                  <td className="text-end">{fmtInt(d.received)}</td>
                  <td className="text-end fw-bold" style={{ color: d.owed > 0 ? '#dc2626' : '#64748b' }}>{fmtInt(d.owed)}</td>
                </tr>
              ))}
              <tr style={{ background: '#f8fafc', fontWeight: 700 }}>
                <td colSpan={3}>{he ? 'סה"כ' : 'Total'}</td>
                <td className="text-end">{fmtInt(tValue)}</td>
                <td className="text-end" style={{ color: '#166534' }}>{fmtInt(tEarned)}</td>
                <td className="text-end">{fmtInt(tReceived)}</td>
                <td className="text-end" style={{ color: tOwed > 0 ? '#dc2626' : '#64748b' }}>{fmtInt(tOwed)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </>);
    }

    if (key === 'profit') {
      return (<>
        <div className="formula" style={{ marginTop: 12 }}>
          <div><span className="lbl">{he ? 'הכנסות (C price)' : 'Income (C price)'}</span><span className="val">{fmtInt(kpis.invoiceIncome)}</span></div>
          <span className="op">+</span>
          <div><span className="lbl">{he ? 'זיכויים שהתקבלו' : 'Credits Received'}</span><span className="val">{fmtInt(kpis.credits)}</span></div>
          <span className="op">−</span>
          <div><span className="lbl">{he ? 'עלות סחורה (COGS)' : 'COGS'}</span><span className="val">{fmtInt(kpis.cogs)}</span></div>
          <span className="op">−</span>
          <div><span className="lbl">{he ? 'ריבייט 1%' : 'Rebate 1%'}</span><span className="val">{fmtInt(kpis.customerRebate)}</span></div>
          {(kpis.importShipping || 0) > 0 && <>
            <span className="op">−</span>
            <div><span className="lbl">{he ? 'שילוח ייבוא' : 'Import Shipping'}</span><span className="val">{fmtInt(kpis.importShipping)}</span></div>
          </>}
          {(kpis.landedCosts || 0) > 0 && <>
            <span className="op">−</span>
            <div><span className="lbl">{he ? 'עלויות נלוות' : 'Landed Costs'}</span><span className="val">{fmtInt(kpis.landedCosts)}</span></div>
          </>}
          <span className="op">=</span>
          <div><span className="lbl">{he ? 'רווח נטו' : 'Net Profit'}</span><span className="val" style={{ color: kpis.profit >= 0 ? 'var(--green)' : 'var(--red)' }}>{fmtInt(kpis.profit)}</span></div>
        </div>
        <div style={{ marginTop: 8, fontSize: 11, color: '#64748b' }}>
          {he
            ? '* הוצאות M (m_expenses) כבר מנוכות מההכנסה לפני שלב זה — לא חוזרים עליהן.'
            : '* M expenses (m_expenses) are already netted out of Income — not subtracted again here.'}
        </div>
      </>);
    }

    if (key === 'quarterlyCredits') {
      const base = kpis.siInvoiceTotal || 0;
      const exportRmaPdf = () => {
        const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
        const qLabel = quarter === 'YTD' ? `YTD ${year}` : `${quarter} ${year}`;
        const rmaAmount = (kpis.defective.isPending ? kpis.defective.expected : kpis.defective.received).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
        const siTotal = base.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
        // XSS-safe: escape strings (defense in depth, all values are internal but
        // safer to escape uniformly). Fix 2026-05-27.
        const esc = (s) => String(s == null ? '' : s).replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' }[c]));
        const w = window.open('', '_blank', 'width=800,height=600');
        w.document.write(`<!DOCTYPE html><html><head><title>RMA Claim - ${esc(qLabel)}</title>
<style>
  body { font-family: Arial, sans-serif; padding: 60px; color: #1a1d23; }
  h1 { font-size: 28px; margin-bottom: 40px; border-bottom: 3px solid #1a1d23; padding-bottom: 16px; }
  table { width: 100%; border-collapse: collapse; margin-top: 30px; }
  th { text-align: left; padding: 12px 16px; background: #f4f5f7; border: 1px solid #dee2e6; font-size: 13px; text-transform: uppercase; letter-spacing: 1px; color: #666; }
  td { padding: 14px 16px; border: 1px solid #dee2e6; font-size: 15px; }
  td.amount { text-align: right; font-weight: 700; font-size: 16px; }
  .footer { margin-top: 60px; font-size: 12px; color: #999; border-top: 1px solid #eee; padding-top: 16px; }
  @media print { body { padding: 40px; } }
</style></head><body>
<h1>RMA Claim — ${esc(qLabel)}</h1>
<table>
  <thead><tr><th>Company</th><th>Date</th><th>Quarter</th><th>Total SI Purchases</th><th>RMA Rate</th><th style="text-align:right">RMA Claim Amount</th></tr></thead>
  <tbody><tr>
    <td>Shager</td>
    <td>${esc(today)}</td>
    <td>${esc(qLabel)}</td>
    <td>${siTotal}</td>
    <td>1.75%</td>
    <td class="amount">${rmaAmount}</td>
  </tr></tbody>
</table>
<div class="footer">Generated by Shager Tracker · ${esc(today)}</div>
</body></html>`);
        w.document.close();
        setTimeout(() => w.print(), 300);
      };
      return (
        <div style={{ marginTop: 12 }}>
          <div className="formula" style={{ marginBottom: 8 }}>
            <div><span className="lbl">{he ? 'סה"כ חשבוניות SI' : 'Total SI Invoices'}</span><span className="val">{fmtInt(base)}</span></div>
            <span className="op">×</span>
            <div><span className="lbl">2%</span><span className="val">{fmtInt(kpis.quarterlyCredit.expected)}</span></div>
            {kpis.quarterlyCredit.received > 0 && <>
              <span className="op">→</span>
              <div><span className="lbl">{he ? 'התקבל בפועל' : 'Received'}</span><span className="val" style={{ color: 'var(--green)' }}>{fmtInt(kpis.quarterlyCredit.received)}</span></div>
            </>}
          </div>
          <div className="formula">
            <div><span className="lbl">{he ? 'סה"כ חשבוניות SI' : 'Total SI Invoices'}</span><span className="val">{fmtInt(base)}</span></div>
            <span className="op">×</span>
            <div><span className="lbl">{he ? 'תקולים 1.75%' : 'Defective 1.75%'}</span><span className="val">{fmtInt(kpis.defective.expected)}</span></div>
            {kpis.defective.received > 0 && <>
              <span className="op">→</span>
              <div><span className="lbl">{he ? 'התקבל בפועל' : 'Received'}</span><span className="val" style={{ color: 'var(--green)' }}>{fmtInt(kpis.defective.received)}</span></div>
            </>}
          </div>
          <div style={{ marginTop: 12, textAlign: 'end' }}>
            <button className="btn btn-outline" onClick={exportRmaPdf}>
              {he ? 'ייצוא דוח RMA (PDF)' : 'Export RMA Report (PDF)'}
            </button>
          </div>
        </div>
      );
    }

    if (key === 'mProfit' && profitDetail) {
      return (<>
        <h4 style={{ marginBottom: 8, color: 'var(--text-muted)', fontSize: 13 }}>M Profit — Per Order (Revenue - Expenses)</h4>
        <div className="table-wrap"><table>
          <thead><tr><th>Order</th><th>Status</th><th className="text-end">M %</th><th className="text-end">M Revenue</th><th className="text-end">M Expenses</th><th className="text-end">M Profit</th></tr></thead>
          <tbody>
            {profitDetail.map(o => {
              const mRev = o.items.reduce((s, i) => s + (i.customerPrice - i.mPrice) * i.quantity, 0);
              const mExp = o.mExpenses || 0;
              return (
                <tr key={o.orderNumber}>
                  <td className="fw-bold">#{o.orderNumber}</td>
                  <td><span className="badge" style={{ background: o.status === 'completed' ? '#dcfce7' : '#e3f2fd', color: o.status === 'completed' ? '#16a34a' : '#1565c0', fontSize: 11 }}>{o.status}</span></td>
                  <td className="text-end">{o.mPercentage || 1.75}%</td>
                  <td className="text-end" style={{ fontFamily: "'DM Mono', monospace" }}>{fmtInt(mRev)}</td>
                  <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", color: '#dc2626' }}>{fmtInt(mExp)}</td>
                  <td className="text-end fw-bold" style={{ fontFamily: "'DM Mono', monospace", color: mRev - mExp >= 0 ? '#2e7d32' : '#dc2626' }}>{fmtInt(mRev - mExp)}</td>
                </tr>
              );
            })}
            <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
              <td colSpan={3}>Total</td>
              <td className="text-end" style={{ fontFamily: "'DM Mono', monospace" }}>{fmtInt(profitDetail.reduce((s, o) => s + o.items.reduce((s2, i) => s2 + (i.customerPrice - i.mPrice) * i.quantity, 0), 0))}</td>
              <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", color: '#dc2626' }}>{fmtInt(profitDetail.reduce((s, o) => s + (o.mExpenses || 0), 0))}</td>
              <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", color: '#2e7d32' }}>{fmtInt(profitDetail.reduce((s, o) => s + o.items.reduce((s2, i) => s2 + (i.customerPrice - i.mPrice) * i.quantity, 0) - (o.mExpenses || 0), 0))}</td>
            </tr>
          </tbody>
        </table></div>
      </>);
    }

    if (key === 'cProfit' && profitDetail) {
      return (<>
        <h4 style={{ marginBottom: 8, color: 'var(--text-muted)', fontSize: 13 }}>C Profit — Per Order</h4>
        <div className="table-wrap"><table>
          <thead><tr><th>Order</th><th>Status</th><th className="text-end">Revenue</th><th className="text-end">C Profit</th></tr></thead>
          <tbody>
            {profitDetail.map(o => {
              const cProfit = o.items.reduce((s, i) => s + (i.mPrice - i.cPrice) * i.quantity, 0);
              return (
                <tr key={o.orderNumber}>
                  <td className="fw-bold">#{o.orderNumber}</td>
                  <td><span className="badge" style={{ background: o.status === 'completed' ? '#dcfce7' : '#e3f2fd', color: o.status === 'completed' ? '#16a34a' : '#1565c0', fontSize: 11 }}>{o.status}</span></td>
                  <td className="text-end">{fmtInt(o.items.reduce((s, i) => s + i.customerPrice * i.quantity, 0))}</td>
                  <td className="text-end fw-bold" style={{ fontFamily: "'DM Mono', monospace", color: '#2e7d32' }}>{fmtInt(cProfit)}</td>
                </tr>
              );
            })}
            <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
              <td colSpan={3}>Total</td>
              <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", color: '#2e7d32' }}>{fmtInt(profitDetail.reduce((s, o) => s + o.items.reduce((s2, i) => s2 + (i.mPrice - i.cPrice) * i.quantity, 0), 0))}</td>
            </tr>
          </tbody>
        </table></div>
      </>);
    }

    return null;
  };

  // Monthly totals
  const totals = months.reduce((acc, m) => ({
    arrivedUnits: acc.arrivedUnits + m.arrived.units,
    arrivedValue: acc.arrivedValue + m.arrived.value,
    soldUnits: acc.soldUnits + m.sold.units,
    revenue: acc.revenue + m.sold.revenue,
    credits: acc.credits + m.credits,
    cogs: acc.cogs + m.cogs,
    profit: acc.profit + m.profit,
  }), { arrivedUnits: 0, arrivedValue: 0, soldUnits: 0, revenue: 0, credits: 0, cogs: 0, profit: 0 });

  const checkBioStatus = async () => {
    try {
      const res = await api.post('/auth/check-user', { username: localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).username : '' });
      setBioStatus(res.data);
    } catch {}
  };

  const registerBiometric = async () => {
    try {
      const optionsRes = await api.post('/auth/webauthn/register-options');
      const credential = await startRegistration({ optionsJSON: optionsRes.data });
      await api.post('/auth/webauthn/register-verify', { credential, deviceName: navigator.userAgent.slice(0, 50) });
      alert(he ? 'ביומטרי נרשם בהצלחה!' : 'Biometric registered successfully!');
      setBioStatus(prev => ({ ...prev, hasBiometric: true }));
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.error || err.message || 'Biometric not supported'));
    }
  };

  return (
    <div>
      {/* Security Settings (mobile accessible) */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button className="btn btn-sm btn-outline" onClick={() => { setShowSecurity(!showSecurity); if (!showSecurity) checkBioStatus(); }}
          style={{ fontSize: 12 }}>
          ⚙️ {he ? 'אבטחה' : 'Security'}
        </button>
      </div>
      {showSecurity && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <h3 style={{ fontSize: 14, marginBottom: 12 }}>🔐 {he ? 'הגדרות אבטחה' : 'Security Settings'}</h3>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13 }}>{he ? 'כניסה ביומטרית' : 'Biometric Login'}</div>
              <div className="text-muted" style={{ fontSize: 11 }}>Face ID / Touch ID / Fingerprint</div>
              {bioStatus?.hasBiometric && (
                <span className="badge" style={{ background: '#e8f5e9', color: '#2e7d32', marginTop: 4 }}>✓ {he ? 'מוגדר' : 'Registered'}</span>
              )}
              {/* The server only knows a credential exists somewhere — a passkey is bound to
                  the machine and browser it was created on, so it says nothing about THIS
                  device. The old copy claimed "active on this device", which is what left a
                  user stranded on a new Mac with no way to enrol. */}
              <div className="text-muted" style={{ fontSize: 11, marginTop: 4 }}>
                {bioStatus?.hasBiometric
                  ? (he ? 'מפתח רשום במערכת. מפתח קשור למחשב ולדפדפן שבהם נוצר — במחשב חדש יש להוסיף מכשיר.'
                        : 'A key is registered. A passkey is bound to the machine and browser that created it — on a new computer, add this device.')
                  : (he ? 'לא נרשם עדיין מפתח' : 'No key registered yet')}
              </div>
            </div>
            <button className="btn btn-gold btn-sm" onClick={registerBiometric}>
              {bioStatus?.hasBiometric
                ? (he ? '➕ הוסף מכשיר זה' : '➕ Add this device')
                : (he ? 'הגדר ביומטרי' : 'Register Biometric')}
            </button>
          </div>
        </div>
      )}

      {/* View tabs: overview / M / C — separate dashboards. Period tabs below apply to all. */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button className={`btn btn-sm ${view === 'overview' ? 'btn-gold' : 'btn-outline'}`}
          style={{ borderRadius: 8, fontWeight: 700 }} onClick={() => setView('overview')}>
          📊 {he ? 'סקירה' : 'Overview'}
        </button>
        <button className={`btn btn-sm ${view === 'm' ? 'btn-gold' : 'btn-outline'}`}
          style={{ borderRadius: 8, fontWeight: 700 }} onClick={() => setView('m')}>
          🅜 {he ? 'דשבורד M' : 'M dashboard'}
        </button>
        <button className={`btn btn-sm ${view === 'c' ? 'btn-gold' : 'btn-outline'}`}
          style={{ borderRadius: 8, fontWeight: 700 }} onClick={() => setView('c')}>
          🅒 {he ? 'דשבורד C' : 'C dashboard'}
        </button>
      </div>

      {/* Quarter Tabs */}
      <div className="sub-tabs" style={{ marginBottom: 20 }}>
        {['Q1', 'Q2', 'Q3', 'Q4'].map(q => (
          <button key={q} className={`sub-tab${quarter === q ? ' active' : ''}`} onClick={() => setQuarter(q)}>
            {q} {year}
          </button>
        ))}
        <button className={`sub-tab${quarter === 'YTD' ? ' active' : ''}`}
          style={{ marginInlineStart: 'auto', fontWeight: 700 }} onClick={() => setQuarter('YTD')}>
          {he ? `${year} שנתי` : `YTD ${year}`}
        </button>
      </div>

      {view === 'm' || view === 'c'
        ? <MCView party={view === 'm' ? 'M' : 'C'} year={year} quarter={quarter} he={he} />
        : (<>

      {/* Section: Inventory Flow */}
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, color: '#9ca3af', fontWeight: 700, marginBottom: 10, borderBottom: '1px solid #e5e7eb', paddingBottom: 6 }}>
        {he ? `תנועת מלאי — ${periodLabel}` : `Inventory Flow — ${periodLabel}`}
      </div>
      <div className="summary-cards" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        {kpiCards.slice(0, 5).map(card => (
          <div key={card.key} className={`summary-card clickable ${card.color}`}
            onClick={() => toggle(card.key)}
            style={{ outline: expandedCard === card.key ? '2px solid var(--gold)' : 'none', position: 'relative' }}>
            <div style={{ position: 'absolute', top: 10, right: 12, fontSize: 10, color: 'var(--text-muted)', opacity: 0.6 }}>
              ⓘ
            </div>
            <div className="label">{card.label}</div>
            <div className="value">{card.value}</div>
            <div className="sub">{card.sub}</div>
            {card.sub2 && (
              <div className="sub" style={{ marginTop: 4, fontSize: 11, color: 'var(--text-muted)', borderTop: '1px solid var(--border)', paddingTop: 6 }}>{card.sub2}</div>
            )}
            {card.badge && (
              <div style={{ display: 'inline-block', marginTop: 8, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, background: card.badgeColor, color: card.badgeText }}>{card.badge}</div>
            )}
          </div>
        ))}
      </div>
      {expandedCard && ['arrived', 'transit', 'sold', 'stock', 'stockValue'].includes(expandedCard) && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>{renderExpandedDetail(expandedCard)}</div>
      )}

      {/* Section 1: Current — Completed + In Process */}
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, color: '#2e7d32', fontWeight: 700, margin: '20px 0 10px', borderBottom: '2px solid #4caf50', paddingBottom: 6 }}>
        {he ? `📊 נוכחי — ${periodLabel}` : `📊 Current — ${periodLabel}`}
      </div>
      <div className="summary-cards" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
        {[
          { key: 'revenue', label: he ? 'מכירות' : 'Sales Revenue', value: fmtInt(kpis.revenue), sub: `${details?.sold?.length || 0} ${he ? 'הזמנות' : 'orders'} · ${he ? 'ללקוח' : 'to Cellcom'}`, color: 'clear-green' },
          { key: 'credits', label: he ? 'זיכויים שהתקבלו' : 'Credits Received', value: fmtInt(kpis.credits), sub: he ? 'פיצויים + MDF' : 'compensations + MDF', color: 'clear-green' },
          { key: 'dealCreditEarned', label: he ? 'זיכוי שהרווחנו מדילים' : 'Deal Credit Earned',
            value: fmtInt(kpis.dealCreditEarned || 0),
            sub: `${he ? 'מתוך' : 'of'} ${fmtInt(kpis.dealCreditTotal || 0)} · ${he ? 'התקבל' : 'received'}: ${fmtInt(kpis.dealCreditReceived || 0)}`,
            color: 'clear-green',
            badge: (kpis.dealTargetsReached && kpis.dealTargetsReached.length > 0) ? (he ? `✓ ${kpis.dealTargetsReached.length} הושלם` : `✓ ${kpis.dealTargetsReached.length} reached`) : null,
            badgeColor: '#dcfce7', badgeText: '#166534' },
          { key: 'mProfit', label: he ? 'רווח M & C' : 'M & C Profit', value: fmtInt((kpis.mProfit || 0) + (kpis.cProfit || 0)), sub: `M: ${fmtInt(kpis.mProfit || 0)} · C: ${fmtInt(kpis.cProfit || 0)}`, color: 'clear-green' },
          { key: 'quarterlyCredits', label: he ? 'קרדיט רבעוני' : 'Quarterly Credits',
            value: fmtInt((kpis.quarterlyCredit.isPending ? kpis.quarterlyCredit.expected : kpis.quarterlyCredit.received) + (kpis.defective.isPending ? kpis.defective.expected : kpis.defective.received)),
            sub: `2%: ${fmtInt(kpis.quarterlyCredit.isPending ? kpis.quarterlyCredit.expected : kpis.quarterlyCredit.received)} · 1.75%: ${fmtInt(kpis.defective.isPending ? kpis.defective.expected : kpis.defective.received)}`,
            color: 'clear-green',
            badge: kpis.quarterlyCredit.isPending ? (he ? '⏳ ממתין' : '⏳ Pending') : (he ? '✓ התקבל' : '✓ Received'),
            badgeColor: kpis.quarterlyCredit.isPending ? '#fff3e0' : '#e8f5e9', badgeText: kpis.quarterlyCredit.isPending ? '#e65100' : '#2e7d32' },
          { key: 'profit', label: he ? 'רווח נוכחי' : 'Current Profit',
            value: fmtInt(kpis.profit),
            sub: he ? 'נטו: זיכויים − הוצאות שילוח' : 'net: credits − shipping/landed',
            color: 'green',
            badge: kpis.margin > 0 ? `${kpis.margin.toFixed(1)}%` : null,
            badgeColor: '#fff', badgeText: '#2e7d32' },
        ].map(card => (
          <div key={card.key} className={`summary-card clickable ${card.color}`}
            onClick={() => toggle(card.key)}
            style={{ outline: expandedCard === card.key ? '2px solid var(--gold)' : 'none', position: 'relative' }}>
            <div style={{ position: 'absolute', top: 10, right: 12, fontSize: 10, color: 'var(--text-muted)', opacity: 0.6 }}>ⓘ</div>
            <div className="label">{card.label}</div>
            {card.customContent ? card.customContent : (<>
              <div className="value">{card.value}</div>
              <div className="sub">{card.sub}</div>
              {card.badge && <div style={{ display: 'inline-block', marginTop: 8, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, background: card.badgeColor, color: card.badgeText }}>{card.badge}</div>}
            </>)}
          </div>
        ))}
      </div>
      {expandedCard && ['quarterlyCredits', 'revenue', 'credits', 'profit', 'mProfit', 'cProfit', 'dealCreditEarned'].includes(expandedCard) && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>{renderExpandedDetail(expandedCard)}</div>
      )}

      {/* Section 2: Expected — Future Orders */}
      {f.units > 0 && (<>
        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, color: '#b8860b', fontWeight: 700, margin: '20px 0 10px', borderBottom: '2px solid #daa520', paddingBottom: 6 }}>
          {he ? `📦 צפוי — הזמנות עתידיות` : `📦 Expected — Future Orders`}
        </div>
        <div className="summary-cards" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
          {[
            { label: he ? 'מכירות צפויות' : 'Expected Sales Revenue', value: fmtInt(f.customerRevenue), sub: `${details?.futureOrders?.length || 0} ${he ? 'הזמנות' : 'orders'} · ${fmtQty(f.units)} ${he ? 'יחידות' : 'units'}`, color: 'clear-gold' },
            { label: he ? 'זיכויים ממתינים' : 'Pending Credits', value: fmtInt(f.pendingCompensations || 0), sub: he ? 'SO שטרם הפכו SI' : 'SO not yet invoiced', color: 'clear-gold' },
            { label: he ? 'רווח M & C' : 'M & C Profit', value: fmtInt((f.mProfit || 0) + (f.cProfit || 0)), sub: `M: ${fmtInt(f.mProfit || 0)} · C: ${fmtInt(f.cProfit || 0)}`, color: 'clear-gold' },
            { label: he ? 'קרדיט רבעוני צפוי' : 'Expected Quarterly Credits', value: fmtInt(f.expectedCredit45 + f.expectedCredit175),
              sub: `2%: ${fmtInt(f.expectedCredit45)} · 1.75%: ${fmtInt(f.expectedCredit175)}`, color: 'clear-gold' },
            { label: he ? 'רווח צפוי' : 'Expected Profit', value: fmtInt(f.totalExpectedProfit),
              sub: he ? 'כולל פיצויים + קרדיטים צפויים' : 'incl. compensations + expected credits',
              color: 'gold-fill',
              badge: f.invoiceIncome > 0 ? `${(f.totalExpectedProfit / f.invoiceIncome * 100).toFixed(1)}%` : null, badgeColor: '#fff', badgeText: '#b8860b' },
          ].map((card, i) => (
            <div key={i} className={`summary-card clickable ${card.color}`} onClick={() => toggle('exp_'+i)}
              style={{ outline: expandedCard === 'exp_'+i ? '2px solid var(--gold)' : 'none', position: 'relative' }}>
              <div style={{ position: 'absolute', top: 10, right: 12, fontSize: 10, color: 'var(--text-muted)', opacity: 0.6 }}>ⓘ</div>
              <div className="label">{card.label}</div>
              {card.customContent ? card.customContent : (<>
                <div className="value">{card.value}</div>
                <div className="sub">{card.sub}</div>
                {card.badge && <div style={{ display: 'inline-block', marginTop: 8, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, background: card.badgeColor, color: card.badgeText }}>{card.badge}</div>}
              </>)}
            </div>
          ))}
        </div>
        {/* Expected details */}
        {expandedCard === 'exp_0' && details?.futureOrders && (
          <div className="card" style={{ padding: 16, marginBottom: 16 }}>
            <h4 style={{ marginBottom: 8, color: '#b8860b', fontSize: 13 }}>Future Orders</h4>
            <div className="table-wrap"><table><thead><tr><th>Order</th><th>Transport</th><th className="text-end">Units</th><th className="text-end">Revenue</th></tr></thead>
            <tbody>{details.futureOrders.map(o => (
              <tr key={o.orderNumber}><td className="fw-bold">#{o.orderNumber}</td><td>{o.transport || '—'}</td><td className="text-end">{fmtQty(o.units)}</td><td className="text-end">{fmtInt(o.revenue)}</td></tr>
            ))}</tbody></table></div>
          </div>
        )}
        {expandedCard === 'exp_1' && details?.credits && (
          <div className="card" style={{ padding: 16, marginBottom: 16 }}>
            <h4 style={{ marginBottom: 8, color: '#b8860b', fontSize: 13 }}>Pending Credits (SO)</h4>
            <div className="table-wrap"><table>
              <thead><tr><th>Order</th><th>Description</th><th className="text-end">Amount</th></tr></thead>
              <tbody>
                {details.credits.filter(c => c.orderNo?.startsWith('SO')).map((c, i) => (
                  <tr key={i}><td className="fw-bold">{c.orderNo}</td><td>{c.description}</td><td className="text-end fw-bold" style={{ fontFamily: "'DM Mono', monospace" }}>{fmtInt(c.amount)}</td></tr>
                ))}
                {details.credits.filter(c => c.orderNo?.startsWith('SO')).length === 0 && (
                  <tr><td colSpan={3} className="text-muted">No pending SO credits</td></tr>
                )}
              </tbody>
            </table></div>
          </div>
        )}
        {expandedCard === 'exp_2' && profitDetail && (
          <div className="card" style={{ padding: 16, marginBottom: 16 }}>
            <h4 style={{ marginBottom: 8, color: '#b8860b', fontSize: 13 }}>M & C Profit — Future Orders</h4>
            <div style={{ fontSize: 14, fontFamily: "'DM Mono', monospace" }}>
              M Profit: <strong>{fmtInt(f.mProfit || 0)}</strong><br/>
              C Profit: <strong>{fmtInt(f.cProfit || 0)}</strong>
            </div>
          </div>
        )}
        {expandedCard === 'exp_3' && (
          <div className="card" style={{ padding: 16, marginBottom: 16 }}>
            <h4 style={{ marginBottom: 8, color: '#b8860b', fontSize: 13 }}>Expected Credits</h4>
            <div style={{ fontSize: 14, fontFamily: "'DM Mono', monospace" }}>
              Future Customer Revenue: {fmtInt(f.customerRevenue)}<br/>
              × 2% Support: <strong>{fmtInt(f.expectedCredit45)}</strong><br/>
              × 1.75% Defective: <strong>{fmtInt(f.expectedCredit175)}</strong><br/>
              = Total Expected: <strong>{fmtInt(f.expectedCredit45 + f.expectedCredit175)}</strong>
            </div>
          </div>
        )}
        {expandedCard === 'exp_4' && (
          <div className="card" style={{ padding: 16, marginBottom: 16 }}>
            <h4 style={{ marginBottom: 8, color: '#b8860b', fontSize: 13 }}>Expected Profit Formula</h4>
            <div style={{ fontSize: 14, fontFamily: "'DM Mono', monospace" }}>
              Shager Income: {fmtInt(f.invoiceIncome)}<br/>
              − COGS: {fmtInt(f.cogs)}<br/>
              − Rebate 1%: {fmtInt(f.customerRebate)}<br/>
              + Pending Credits: {fmtInt(f.pendingCompensations || 0)}<br/>
              + Expected 2%: {fmtInt(f.expectedCredit45)}<br/>
              + Expected 1.75%: {fmtInt(f.expectedCredit175)}<br/>
              = <strong>Expected Profit: {fmtInt(f.totalExpectedProfit)}</strong>
            </div>
          </div>
        )}
      </>)}

      {/* Section 3: Total */}
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, color: '#0071e3', fontWeight: 700, margin: '20px 0 10px', borderBottom: '2px solid #0071e3', paddingBottom: 6 }}>
        {he ? `📈 סה"כ — נוכחי + צפוי` : `📈 Total — Current + Expected`}
      </div>
      <div className="summary-cards" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
        {[
          { label: he ? 'סה"כ מכירות' : 'Total Revenue', value: fmtInt(kpis.revenue + f.customerRevenue),
            sub: `${details?.sold?.length || 0} ${he ? 'הזמנות' : 'orders'} + ${details?.futureOrders?.length || 0} ${he ? 'ממתינות' : 'pending'} = ${(details?.sold?.length || 0) + (details?.futureOrders?.length || 0)} ${he ? 'סה"כ' : 'total'}`, color: 'clear-blue' },
          { label: he ? 'סה"כ רווח' : 'Total Profit',
            value: fmtInt(kpis.profit + f.totalExpectedProfit),
            sub: `${he ? 'נוכחי' : 'current'}: ${fmtInt(kpis.profit)} + ${he ? 'צפוי' : 'expected'}: ${fmtInt(f.totalExpectedProfit)}`,
            color: 'blue-fill',
            badge: (() => {
              const total = kpis.profit + f.totalExpectedProfit;
              const totalIncome = kpis.invoiceIncome + f.invoiceIncome;
              const m = totalIncome > 0 ? (total / totalIncome * 100) : 0;
              return m > 0 ? `${m.toFixed(1)}%` : null;
            })(),
            badgeColor: '#dbeafe', badgeText: '#0071e3' },
          { label: he ? 'רווח כולל (הכל)' : 'Total Profit (All-In)',
            value: fmtInt(
              kpis.profit
              + (kpis.mProfit || 0) + (kpis.cProfit || 0)
              + (kpis.quarterlyCredit.isPending ? kpis.quarterlyCredit.expected : kpis.quarterlyCredit.received)
              + (kpis.defective.isPending ? kpis.defective.expected : kpis.defective.received)
            ),
            sub: he ? 'שגר + M&C + קרדיט רבעוני' : 'Shager + M&C + Q-credits',
            color: 'blue-fill',
            badge: (() => {
              const allIn = kpis.profit + (kpis.mProfit || 0) + (kpis.cProfit || 0)
                + (kpis.quarterlyCredit.isPending ? kpis.quarterlyCredit.expected : kpis.quarterlyCredit.received)
                + (kpis.defective.isPending ? kpis.defective.expected : kpis.defective.received);
              const m = kpis.revenue > 0 ? (allIn / kpis.revenue * 100) : 0;
              return m > 0 ? `${m.toFixed(1)}%` : null;
            })(),
            badgeColor: '#dbeafe', badgeText: '#0071e3' },
        ].map((card, i) => (
          <div key={i} className={`summary-card clickable ${card.color}`} onClick={() => toggle('tot_'+i)}
            style={{ outline: expandedCard === 'tot_'+i ? '2px solid var(--gold)' : 'none', position: 'relative' }}>
            <div style={{ position: 'absolute', top: 10, right: 12, fontSize: 10, color: 'var(--text-muted)', opacity: 0.6 }}>ⓘ</div>
            <div className="label">{card.label}</div>
            <div className="value">{card.value}</div>
            <div className="sub">{card.sub}</div>
            {card.badge && <div style={{ display: 'inline-block', marginTop: 8, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, background: card.badgeColor, color: card.badgeText }}>{card.badge}</div>}
          </div>
        ))}
      </div>
      {/* Total details */}
      {expandedCard === 'tot_0' && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <h4 style={{ marginBottom: 8, color: '#0071e3', fontSize: 13 }}>Total Revenue Breakdown</h4>
          <div style={{ fontSize: 14, fontFamily: "'DM Mono', monospace" }}>
            Current Revenue: {fmtInt(kpis.revenue)}<br/>
            + Future Revenue: {fmtInt(f.customerRevenue)}<br/>
            = <strong>Total: {fmtInt(kpis.revenue + f.customerRevenue)}</strong>
          </div>
        </div>
      )}
      {expandedCard === 'tot_1' && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <h4 style={{ marginBottom: 8, color: '#0071e3', fontSize: 13 }}>Total Profit Breakdown</h4>
          <div style={{ fontSize: 14, fontFamily: "'DM Mono', monospace" }}>
            Current Profit: {fmtInt(kpis.profit)}<br/>
            + Expected Profit: {fmtInt(f.totalExpectedProfit)}<br/>
            = <strong>Total: {fmtInt(kpis.profit + f.totalExpectedProfit)}</strong>
          </div>
        </div>
      )}
      {expandedCard === 'tot_2' && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <h4 style={{ marginBottom: 8, color: '#0071e3', fontSize: 13 }}>{he ? 'רווח כולל (הכל) — פירוט' : 'Total Profit (All-In) Breakdown'}</h4>
          <div style={{ fontSize: 14, fontFamily: "'DM Mono', monospace" }}>
            {he ? 'רווח נוכחי (שגר)' : 'Current Profit (Shager)'}: {fmtInt(kpis.profit)}<br/>
            + {he ? 'רווח M & C' : 'M & C Profit'}: {fmtInt((kpis.mProfit || 0) + (kpis.cProfit || 0))} <span style={{ color: 'var(--text-muted)' }}>(M: {fmtInt(kpis.mProfit || 0)} · C: {fmtInt(kpis.cProfit || 0)})</span><br/>
            + {he ? 'קרדיט רבעוני' : 'Quarterly Credits'}: {fmtInt((kpis.quarterlyCredit.isPending ? kpis.quarterlyCredit.expected : kpis.quarterlyCredit.received) + (kpis.defective.isPending ? kpis.defective.expected : kpis.defective.received))} <span style={{ color: 'var(--text-muted)' }}>(2% + 1.75%{kpis.quarterlyCredit.isPending ? he ? ' · צפוי' : ' · expected' : ''})</span><br/>
            = <strong>{he ? 'סה"כ הכל' : 'Total All-In'}: {fmtInt(kpis.profit + (kpis.mProfit || 0) + (kpis.cProfit || 0) + (kpis.quarterlyCredit.isPending ? kpis.quarterlyCredit.expected : kpis.quarterlyCredit.received) + (kpis.defective.isPending ? kpis.defective.expected : kpis.defective.received))}</strong>
          </div>
        </div>
      )}

      {/* Monthly Breakdown Table */}
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, color: 'var(--text-muted)', fontWeight: 700, margin: '24px 0 10px' }}>
        {he ? 'פירוט חודשי' : 'Monthly Breakdown'}
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>{he ? 'חודש' : 'Month'}</th>
              <th className="text-end">{he ? "הגיעו (יח')" : 'Arrived (units)'}</th>
              <th className="text-end">{he ? 'הגיעו ($)' : 'Arrived ($)'}</th>
              <th className="text-end">{he ? "נמכרו (יח')" : 'Sold (units)'}</th>
              <th className="text-end">{he ? 'מכירות' : 'Revenue'}</th>
              <th className="text-end">{he ? 'זיכויים' : 'Credits'}</th>
              <th className="text-end">{he ? 'עלות' : 'COGS'}</th>
              <th className="text-end">{he ? 'רווח' : 'Profit'}</th>
              <th className="text-end">{he ? 'מרווח' : 'Margin'}</th>
            </tr>
          </thead>
          <tbody>
            {months.map((m) => {
              const hasData = m.arrived.units > 0 || m.sold.units > 0 || m.sold.revenue > 0;
              if (!hasData && quarter === 'YTD') return null;
              return (
                <tr key={m.month}>
                  <td style={{ fontWeight: 700 }}>{monthLabel(m.month, lang)}</td>
                  <td className="text-end" style={{ fontFamily: "'DM Mono', monospace" }}>{m.arrived.units > 0 ? fmtQty(m.arrived.units) : '—'}</td>
                  <td className="text-end" style={{ fontFamily: "'DM Mono', monospace" }}>{m.arrived.value > 0 ? fmtInt(m.arrived.value) : '—'}</td>
                  <td className="text-end" style={{ fontFamily: "'DM Mono', monospace" }}>{m.sold.units > 0 ? fmtQty(m.sold.units) : '—'}</td>
                  <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", color: 'var(--green)' }}>{m.sold.revenue > 0 ? fmtInt(m.sold.revenue) : '—'}</td>
                  <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", color: 'var(--blue)' }}>{m.credits > 0 ? fmtInt(m.credits) : '—'}</td>
                  <td className="text-end" style={{ fontFamily: "'DM Mono', monospace" }}>{m.cogs > 0 ? fmtInt(m.cogs) : '—'}</td>
                  <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", color: m.profit >= 0 ? 'var(--green)' : 'var(--red)' }}>{m.profit !== 0 ? fmtInt(m.profit) : '—'}</td>
                  <td className="text-end">
                    {m.margin > 0 ? (
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 700,
                        background: m.margin >= 15 ? '#e8f5e9' : m.margin >= 0 ? '#fff3e0' : '#fbe9e7',
                        color: m.margin >= 15 ? '#2e7d32' : m.margin >= 0 ? '#e65100' : '#c62828',
                      }}>{m.margin.toFixed(1)}%</span>
                    ) : '—'}
                  </td>
                </tr>
              );
            })}
            <tr style={{ fontWeight: 800, borderTop: '2px solid var(--text)' }}>
              <td>{he ? `סה"כ ${quarter}` : `${quarter} Total`}</td>
              <td className="text-end" style={{ fontFamily: "'DM Mono', monospace" }}>{fmtQty(totals.arrivedUnits)}</td>
              <td className="text-end" style={{ fontFamily: "'DM Mono', monospace" }}>{fmtInt(totals.arrivedValue)}</td>
              <td className="text-end" style={{ fontFamily: "'DM Mono', monospace" }}>{fmtQty(totals.soldUnits)}</td>
              <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", color: 'var(--green)' }}>{fmtInt(totals.revenue)}</td>
              <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", color: 'var(--blue)' }}>{fmtInt(totals.credits)}</td>
              <td className="text-end" style={{ fontFamily: "'DM Mono', monospace" }}>{fmtInt(totals.cogs)}</td>
              <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", color: totals.profit >= 0 ? 'var(--green)' : 'var(--red)' }}>{fmtInt(totals.profit)}</td>
              <td className="text-end">
                {/* Margin denominator = invoice income (Shager C-price income),
                    same basis as the headline kpis.margin. Once the backend makes
                    totals.profit == kpis.profit, this footer % equals the headline. */}
                {kpis.invoiceIncome > 0 ? (
                  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 700, background: '#e8f5e9', color: '#2e7d32' }}>
                    {(totals.profit / kpis.invoiceIncome * 100).toFixed(1)}%
                  </span>
                ) : '—'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      </>)}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// M / C CHAIN VIEW — Shager → C → M → Cellcom.
// Each layer invoices the next; this shows what each is invoiced and what it keeps.
// Read-only: same period scope as the quarter tabs above.
// ══════════════════════════════════════════════════════════════════════════════
// Rendered once per party ('M' or 'C') so each side gets its own dashboard tab.
// M is variable per deal (gross / expenses / net); C is a flat 1.5%.
function MCView({ party, year, quarter, he }) {
  const isM = party === 'M';
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());   // order keys ticked for the report

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/dashboard/mc?year=${year}&quarter=${quarter}`)
      .then(r => { if (alive) { setD(r.data); setSelected(new Set()); } })
      .catch(() => { if (alive) setD(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [year, quarter]);

  // Switching party keeps the fetched data but clears any tick marks.
  useEffect(() => { setSelected(new Set()); }, [party]);

  if (loading) return <div className="loading"><div className="spinner" />{he ? 'טוען…' : 'Loading…'}</div>;
  if (!d) return <div className="empty-state">{he ? 'שגיאה בטעינה' : 'Failed to load'}</div>;

  const t = d.totals;
  const mono = { fontFamily: "'DM Mono', monospace" };

  const allKeys = d.byOrder.map(r => r.key);
  const allSelected = allKeys.length > 0 && allKeys.every(k => selected.has(k));
  const toggleOne = (k) => setSelected(p => { const s = new Set(p); s.has(k) ? s.delete(k) : s.add(k); return s; });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allKeys));
  // No tick = export everything in the period (matches LogisticsPage behaviour).
  const reportRows = () => d.byOrder.filter(r => selected.size === 0 || selected.has(r.key));

  const exportReport = () => {
    const rows = reportRows();
    if (!rows.length) return;
    const sum = (f) => rows.reduce((s, r) => s + (Number(r[f]) || 0), 0);
    const L = {
      order: he ? 'הזמנה' : 'Order', status: he ? 'סטטוס' : 'Status', units: he ? "יח'" : 'Units',
      invM: he ? 'חשבונית ל-M' : 'Invoiced to M', gross: he ? 'רווח' : 'Gross',
      exp: he ? 'הוצאות' : 'Expenses', net: he ? 'רווח נטו' : 'Net profit',
      invC: he ? 'חשבונית ל-C' : 'Invoiced to C', cm: he ? 'מרווח C' : 'C margin',
      total: he ? 'סה"כ' : 'TOTAL',
    };
    const body = rows.map(r => isM ? {
      [L.order]: r.key, [L.status]: r.status, 'M%': Number(r.mPct),
      [L.invM]: r.invToM, [L.gross]: r.mGross, [L.exp]: -r.mExpenses, [L.net]: r.mMargin, [L.units]: r.units,
    } : {
      [L.order]: r.key, [L.status]: r.status,
      [L.invC]: r.invToC, [L.cm]: r.cMargin, [L.units]: r.units,
    });
    body.push({});
    body.push(isM ? {
      [L.order]: L.total, [L.status]: '', 'M%': '',
      [L.invM]: sum('invToM'), [L.gross]: sum('mGross'), [L.exp]: -sum('mExpenses'),
      [L.net]: sum('mMargin'), [L.units]: sum('units'),
    } : {
      [L.order]: L.total, [L.status]: '',
      [L.invC]: sum('invToC'), [L.cm]: sum('cMargin'), [L.units]: sum('units'),
    });

    const ws = XLSX.utils.json_to_sheet(body);
    ws['!cols'] = isM
      ? [{ wch: 12 }, { wch: 12 }, { wch: 8 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 10 }]
      : [{ wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 14 }, { wch: 10 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, party);
    XLSX.writeFile(wb, `${party}-report-${d.period.quarter}-${d.period.year}.xlsx`);
  };

  const card = (label, value, sub, color) => (
    <div className="summary-card" style={{ position: 'relative' }}>
      <div className="label">{label}</div>
      <div className="value" style={color ? { color } : undefined}>{value}</div>
      <div className="sub">{sub}</div>
    </div>
  );
  const sectionTitle = (txt, mt) => (
    <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, color: '#9ca3af', fontWeight: 700,
      marginBottom: 10, marginTop: mt || 0, borderBottom: '1px solid #e5e7eb', paddingBottom: 6 }}>{txt}</div>
  );

  return (
    <div>
      {/* Where this party sits in the chain */}
      {sectionTitle(`${he ? 'שרשרת החיוב' : 'Invoice Chain'} — ${d.period.quarter} ${d.period.year}`)}
      <div className="card" style={{ padding: 16, marginBottom: 20, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        {[
          { lbl: he ? 'שגר ← C' : 'Shager → C', val: t.invToC, sub: he ? 'חשבונית ל-C' : 'invoiced to C', on: !isM },
          { lbl: he ? 'C ← M' : 'C → M', val: t.invToM, sub: he ? 'חשבונית ל-M' : 'invoiced to M', on: isM },
          { lbl: he ? 'M ← סלקום' : 'M → Cellcom', val: t.cellcomRev, sub: he ? 'חשבונית לסלקום' : 'invoiced to Cellcom', on: false },
        ].map((s, i) => (
          <Fragment key={s.lbl}>
            {i > 0 && <div style={{ fontSize: 20, color: '#d1d5db' }}>→</div>}
            <div style={{ textAlign: 'center', flex: 1, minWidth: 130,
              padding: 8, borderRadius: 8, background: s.on ? 'rgba(212,175,55,0.12)' : 'transparent' }}>
              <div className="text-muted" style={{ fontSize: 11 }}>{s.lbl}</div>
              <div style={{ ...mono, fontSize: 18, fontWeight: 800 }}>{fmtInt(s.val)}</div>
              <div className="text-muted" style={{ fontSize: 10 }}>{s.sub}</div>
            </div>
          </Fragment>
        ))}
      </div>

      {isM ? (
        <>
          {sectionTitle(`M — ${he ? 'רווחיות משתנה לפי עסקה' : 'variable per deal'}`)}
          <div className="summary-cards" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            {card(he ? 'סה"כ חשבוניות ל-M' : 'Total invoiced to M', fmtInt(t.invToM), `${fmtQty(t.units)} ${he ? "יח'" : 'units'}`)}
            {card(he ? 'רווח' : 'Gross profit', fmtInt(t.mGross), he ? 'המרווח המלא ש-M גובה' : 'full spread M captures', 'var(--blue)')}
            {card(he ? 'הוצאות' : 'Expenses', `-${fmtInt(t.mExpenses)}`, he ? 'משתנות לפי עסקה' : 'vary per deal', 'var(--red)')}
            {card(he ? 'רווח נטו' : 'Net profit', fmtInt(t.mMargin), he ? 'עמלת M% נטו' : 'M% commission, net', 'var(--green)')}
            {d.mPctStats && card(he ? 'טווח M%' : 'M% range', `${d.mPctStats.min}% – ${d.mPctStats.max}%`,
              he ? `ממוצע משוקלל ${d.mPctStats.weightedAvg}% · ${d.mPctStats.orders} עסקאות`
                 : `weighted avg ${d.mPctStats.weightedAvg}% · ${d.mPctStats.orders} deals`)}
          </div>
          <div className="text-muted" style={{ fontSize: 11, marginTop: 8 }}>
            {he ? `רווח ${fmtInt(t.mGross)} − הוצאות ${fmtInt(t.mExpenses)} = רווח נטו ${fmtInt(t.mMargin)}`
                : `Gross ${fmtInt(t.mGross)} − expenses ${fmtInt(t.mExpenses)} = net ${fmtInt(t.mMargin)}`}
          </div>
        </>
      ) : (
        <>
          {sectionTitle(`C — ${he ? `רווחיות קבועה ${d.config.cPct}%` : `fixed ${d.config.cPct}%`}`)}
          <div className="summary-cards" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            {card(he ? 'סה"כ חשבוניות ל-C' : 'Total invoiced to C', fmtInt(t.invToC), he ? 'הכנסת שגר' : "Shager's revenue")}
            {card(he ? 'מרווח C' : 'C margin', fmtInt(t.cMargin), `${d.config.cPct}% ${he ? 'קבוע בכל העסקאות' : 'fixed on every deal'}`, 'var(--blue)')}
            {card(he ? "יח'" : 'Units', fmtQty(t.units), `${d.byOrder.length} ${he ? 'עסקאות' : 'deals'}`)}
          </div>
          <div className="text-muted" style={{ fontSize: 11, marginTop: 8 }}>
            {he ? `C גובה ${d.config.cPct}% קבוע — אין שונות בין עסקאות.` : `C takes a fixed ${d.config.cPct}% — no variance between deals.`}
          </div>
        </>
      )}

      {/* Report builder — tick orders, export this party. No tick = whole period. */}
      {sectionTitle(`${party} — ${he ? 'לפי עסקה' : 'By Deal'}`, 24)}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <span className="text-muted" style={{ fontSize: 12 }}>
          {selected.size > 0
            ? (he ? `${selected.size} הזמנות סומנו` : `${selected.size} orders selected`)
            : (he ? 'לא סומן כלום — הדוח יכלול את כל התקופה' : 'nothing ticked — report covers the whole period')}
        </span>
        <div style={{ flex: 1 }} />
        {selected.size > 0 && (
          <button className="btn btn-sm btn-outline" onClick={() => setSelected(new Set())}>
            {he ? 'נקה בחירה' : 'Clear'}
          </button>
        )}
        <button className="btn btn-sm btn-gold" onClick={exportReport}>
          📄 {he ? `דוח ${party}` : `${party} report`}
        </button>
      </div>
      <div className="table-wrap" style={{ marginBottom: 20 }}>
        <table>
          <thead><tr>
            <th style={{ width: 32 }}>
              <input type="checkbox" checked={allSelected} onChange={toggleAll} title={he ? 'בחר הכל' : 'Select all'} />
            </th>
            <th>{he ? 'הזמנה' : 'Order'}</th>
            <th>{he ? 'סטטוס' : 'Status'}</th>
            {isM && <th className="text-end">M%</th>}
            <th className="text-end">{isM ? (he ? 'חשבונית ל-M' : 'Inv. to M') : (he ? 'חשבונית ל-C' : 'Inv. to C')}</th>
            {isM ? <>
              <th className="text-end">{he ? 'רווח' : 'Gross'}</th>
              <th className="text-end">{he ? 'הוצאות' : 'Expenses'}</th>
              <th className="text-end">{he ? 'רווח נטו' : 'Net'}</th>
            </> : <th className="text-end">{he ? 'מרווח C' : 'C margin'}</th>}
            <th className="text-end">{he ? "יח'" : 'Units'}</th>
          </tr></thead>
          <tbody>
            {d.byOrder.map(r => (
              <tr key={r.key} style={selected.has(r.key) ? { background: 'rgba(212,175,55,0.10)' } : undefined}>
                <td><input type="checkbox" checked={selected.has(r.key)} onChange={() => toggleOne(r.key)} /></td>
                <td style={{ fontWeight: 700 }}>{r.key}</td>
                <td><span className="badge" style={{ fontSize: 10 }}>{r.status}</span></td>
                {/* M% is negotiated per deal — shown as plain fact, not judged */}
                {isM && <td className="text-end" style={{ ...mono, fontWeight: 700 }}>{Number(r.mPct).toFixed(2)}%</td>}
                <td className="text-end" style={mono}>{fmtInt(isM ? r.invToM : r.invToC)}</td>
                {isM ? <>
                  <td className="text-end" style={{ ...mono, color: 'var(--blue)' }}>{fmtInt(r.mGross)}</td>
                  <td className="text-end" style={{ ...mono, color: r.mExpenses > 0 ? 'var(--red)' : undefined }}>{r.mExpenses > 0 ? '-' + fmtInt(r.mExpenses) : '—'}</td>
                  <td className="text-end" style={{ ...mono, color: 'var(--green)', fontWeight: 700 }}>{fmtInt(r.mMargin)}</td>
                </> : <td className="text-end" style={{ ...mono, color: 'var(--blue)' }}>{fmtInt(r.cMargin)}</td>}
                <td className="text-end" style={mono}>{fmtQty(r.units)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* By month */}
      {sectionTitle(he ? 'לפי חודש' : 'By Month')}
      <div className="table-wrap" style={{ marginBottom: 20 }}>
        <table>
          <thead><tr>
            <th>{he ? 'חודש' : 'Month'}</th>
            <th className="text-end">{isM ? (he ? 'חשבונית ל-M' : 'Inv. to M') : (he ? 'חשבונית ל-C' : 'Inv. to C')}</th>
            {isM ? <>
              <th className="text-end">{he ? 'רווח' : 'Gross'}</th>
              <th className="text-end">{he ? 'הוצאות' : 'Expenses'}</th>
              <th className="text-end">{he ? 'רווח נטו' : 'Net'}</th>
            </> : <th className="text-end">{he ? 'מרווח C' : 'C margin'}</th>}
            <th className="text-end">{he ? "יח'" : 'Units'}</th>
          </tr></thead>
          <tbody>
            {d.byMonth.map(r => (
              <tr key={r.key}>
                <td style={{ fontWeight: 700 }}>{monthLabel(r.key, he ? 'he' : 'en')}</td>
                <td className="text-end" style={mono}>{fmtInt(isM ? r.invToM : r.invToC)}</td>
                {isM ? <>
                  <td className="text-end" style={{ ...mono, color: 'var(--blue)' }}>{fmtInt(r.mGross)}</td>
                  <td className="text-end" style={{ ...mono, color: r.mExpenses > 0 ? 'var(--red)' : undefined }}>{r.mExpenses > 0 ? '-' + fmtInt(r.mExpenses) : '—'}</td>
                  <td className="text-end" style={{ ...mono, color: 'var(--green)', fontWeight: 700 }}>{fmtInt(r.mMargin)}</td>
                </> : <td className="text-end" style={{ ...mono, color: 'var(--blue)' }}>{fmtInt(r.cMargin)}</td>}
                <td className="text-end" style={mono}>{fmtQty(r.units)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* By status */}
      {sectionTitle(he ? 'לפי סטטוס' : 'By Status')}
      <div className="table-wrap">
        <table>
          <thead><tr>
            <th>{he ? 'סטטוס' : 'Status'}</th>
            <th className="text-end">{isM ? (he ? 'חשבונית ל-M' : 'Inv. to M') : (he ? 'חשבונית ל-C' : 'Inv. to C')}</th>
            {isM ? <th className="text-end">{he ? 'רווח נטו' : 'Net'}</th>
                 : <th className="text-end">{he ? 'מרווח C' : 'C margin'}</th>}
            <th className="text-end">{he ? "יח'" : 'Units'}</th>
          </tr></thead>
          <tbody>
            {d.byStatus.map(r => (
              <tr key={r.key}>
                <td style={{ fontWeight: 700 }}>{r.key}</td>
                <td className="text-end" style={mono}>{fmtInt(isM ? r.invToM : r.invToC)}</td>
                <td className="text-end" style={{ ...mono, color: 'var(--blue)' }}>{fmtInt(isM ? r.mMargin : r.cMargin)}</td>
                <td className="text-end" style={mono}>{fmtQty(r.units)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
